import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';

import { skip, Subscription, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { PanSearchService } from '../../../services/pan/pan.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-pan-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './pan-search.component.html',
  styleUrl: './pan-search.component.css',
})
export class PanSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;
  singlePan: string = '';
  allJobResultsSubscription?: Subscription;
  routerEventsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];
  searchLoading = false;
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;
  recentResultsSelectAll = { isSelected: false };

  selectedFilterType: string = 'pan';
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  filterTypes: any = [
    { label: 'PAN', value: 'pan' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  panFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.pan_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private panSearchService: PanSearchService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) { }

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if PAN service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.pan;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'panSearchCreditCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (
          event.url === '/pan-search' ||
          event.url.startsWith('/pan-search?')
        ) {
          this.getRecentResults();
        }
      });
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  searchPan() {
    const validationResult = this.validatePan(this.singlePan);
    if (!validationResult.valid) {
      this.toastr.error(validationResult.error || 'Invalid PAN Number');
      return;
    }
    if (this.searchLoading) {
      return;
    }
    this.searchLoading = true;
    const { ip, location } = this.retrieveIPAndLocation();
    let payload: any = {
      pan_number: this.singlePan.trim().replace(/\s+/g, '').toUpperCase(),
      device: 'Chrome',
    };

    if (ip != '') {
      payload.ip_address = ip;
    }

    if (location != '') {
      payload.location = location;
    }

    this.panSearchService
      .searchPan(payload)
      .subscribe(
        (data: any) => {
          this.searchLoading = false;

          const jobId = data?.data?.job_id;
          if (jobId) {
            this.jobIds = [jobId];

            this.searchPollingService.startPolling(this.jobIds, (jobIds) =>
              this.panSearchService.getSearchResults(jobIds),
            );
            this.searchPollingService.ensureBackgroundPolling(
              [
                {
                  job_id: jobId,
                  searchType: 'pan',
                  label: String(payload.pan_number || jobId),
                },
              ],
              3 * 1000,
            );
            this.getAllJobResults();
          }

          this.getRecentResults();
          this.singlePan = '';
        },
        (error) => {
          this.searchLoading = false;
          let errorMessage = 'Unable to Search PAN Details';
          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }
          this.toastr.error(errorMessage);
        },
      );
  }

  validatePan(pan: string): { valid: boolean; error?: string } {
    // Mirror Joi schema: required string, pattern ABCDE1234F
    if (pan === undefined || pan === null) {
      return { valid: false, error: 'PAN number is required' };
    }

    if (typeof pan !== 'string') {
      return { valid: false, error: 'PAN number must be a string' };
    }

    const trimmedPan = pan.trim().replace(/\s+/g, '').toUpperCase();

    if (trimmedPan.length === 0) {
      return { valid: false, error: 'PAN number cannot be empty' };
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(trimmedPan)) {
      return {
        valid: false,
        error:
          'PAN number must be in format: ABCDE1234F (5 letters, 4 digits, 1 letter)',
      };
    }

    return { valid: true };
  }

  onPanInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.toUpperCase();

    // Remove any non-alphanumeric characters (formatting only)
    value = value.replace(/[^A-Z0-9]/g, '');

    // Update the input value
    input.value = value;
    this.singlePan = value;
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => { },
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;
    this.panSearchService
      .getPanSearchHistory(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response: any) => {
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error: any) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.pan_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) => recentResultPaged.pan_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.panSearchService
      .deletePanSearchHistory({
        pan_search_ids: this.selectedRecentResults,
      })
      .subscribe({
        next: (response) => {
          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();
          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.pan_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.panFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id &&
          (!result.done ||
            result.done === false ||
            result.status === 'pending'),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.panSearchService.getSearchResults(jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  getAllJobResults() { }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate([`/pan-search-result/${searchResult.job_id}`], {
      state: { searchResult },
    });
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onPanFilterChange(pan: string) {
    const trimmedPan = pan.trim().toUpperCase();

    if (trimmedPan === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedPan.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_pan';
      this.filterValue = trimmedPan;

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'PAN Search ID': recentResultPaged.pan_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED PAN': recentResultPaged.searched_pan,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `pan-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'PAN Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `pan-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'PAN Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `pan-results-${timestamp || 'export'}`;
    const title = `PAN results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
